import { useState } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import { ChevronDown, ArrowRight } from "lucide-react";
import { FadeUp, AccordionContent } from "../hooks/motion";
import { CALCOM_DEMO_URL } from "../config/demo";

const AccordionItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#1E1E2E]">
      <button
        onClick={() => setOpen(!open)}
        data-testid="faq-accordion-item"
        className="w-full text-left py-5 flex items-center justify-between gap-4"
      >
        <span className="text-[#F4F4F8] font-medium">{question}</span>
        <ChevronDown
          className={`text-[#8B8BA7] w-5 h-5 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AccordionContent open={open}>
        <p className="text-[#8B8BA7] leading-relaxed">{answer}</p>
      </AccordionContent>
    </div>
  );
};

const FAQ_DATA = [
  {
    category: "About CleanBid",
    color: "green",
    questions: [
      {
        q: "What is CleanBid?",
        a: "CleanBid is a revenue platform for home service businesses — cleaning, pest control, lawn care, exterior services, and similar trades. It helps owner-operators close more jobs and collect more 5-star reviews through SMS-driven automation.",
      },
      {
        q: "Who is CleanBid built for?",
        a: "CleanBid is built for owner-operators and small teams with 1–8 people. If you're currently running your business off a personal cell phone and a spreadsheet, CleanBid is exactly what you need.",
      },
      {
        q: "How is CleanBid different from Jobber or Housecall Pro?",
        a: "Jobber and Housecall Pro are job management tools. CleanBid is a revenue tool. The focus isn't scheduling — it's closing. SMS quoting, automated follow-ups, and review collection are at the core of everything we build.",
      },
      {
        q: "Do I need to download an app?",
        a: "No. CleanBid works from any browser on any device. Your customers don't need to download anything either — they interact entirely via SMS.",
      },
    ],
  },
  {
    category: "Quoting & SMS",
    color: "green",
    questions: [
      {
        q: "How does SMS quoting work?",
        a: "When a customer reaches out, you open CleanBid, select your service, set the price, and tap send. The customer gets a clean, professional quote via SMS. They can accept with one tap. The whole process takes under 60 seconds.",
      },
      {
        q: "What SMS number do my customers text?",
        a: "You'll get a dedicated local business number through CleanBid. Your personal phone number stays private, and all business conversations flow through your CleanBid inbox.",
      },
      {
        q: "Can I customize my quote templates?",
        a: "Yes. You can set up service templates with default pricing that you can adjust per job. The goal is 60-second quotes, not copy-pasting the same message every time.",
      },
    ],
  },
  {
    category: "Follow-Ups",
    color: "indigo",
    questions: [
      {
        q: "How do follow-ups work?",
        a: "When you send a quote, CleanBid automatically schedules a follow-up sequence. A friendly reminder on day 1, a value message on day 3, and a final nudge on day 5. The sequence stops automatically the moment the customer responds.",
      },
      {
        q: "Can I customize the follow-up sequences?",
        a: "Yes, on Growth and Pro plans. You can customize the message content, timing, and number of touches. On Starter, you get the default sequences which work well out of the box.",
      },
      {
        q: "What if a customer says no?",
        a: "If a customer responds saying they're not interested, CleanBid marks the lead as closed and stops all follow-ups immediately. No awkward double-messages.",
      },
    ],
  },
  {
    category: "Reviews",
    color: "amber",
    questions: [
      {
        q: "When are review requests sent?",
        a: "Automatically when you mark a job as complete in CleanBid. No manual steps, no forgetting. The request goes out while the experience is still fresh.",
      },
      {
        q: "Which review platforms does CleanBid support?",
        a: "Google Business Profile, Yelp, and Facebook. You choose which platform to direct customers to, or set up smart routing based on where you need reviews most.",
      },
      {
        q: "What if a customer had a bad experience?",
        a: "CleanBid's review requests are designed to prompt direct feedback first. If a customer signals a negative experience, the system flags it for you rather than pushing them to a public review.",
      },
    ],
  },
  {
    category: "Pricing & Contract",
    color: "green",
    questions: [
      {
        q: "Is there a free trial?",
        a: "No. We don't offer free trials because we've found they don't serve owner-operators well. Instead, book a demo — we'll show you exactly how CleanBid works for your business, and you can start with full access from day one.",
      },
      {
        q: "Are there any long-term contracts?",
        a: "No. CleanBid is month-to-month on all plans. Cancel anytime, no questions asked, no exit fees.",
      },
      {
        q: "What happens if I go over my quote limit on Starter?",
        a: "We'll notify you before you hit your limit. You can upgrade to Growth for unlimited quotes, or we'll work with you to figure out the right solution.",
      },
      {
        q: "Is there a setup fee?",
        a: "No setup fees on any plan. You pay your monthly rate and that's it.",
      },
    ],
  },
];

export default function FAQPage() {
  usePageTitle("FAQ | CleanBid.ai");
  return (
    <div className="bg-[#0A0A0F] pt-20">
      {/* Hero */}
      <section className="py-24 text-center">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <FadeUp>
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">FAQ</span>
          </FadeUp>
          <FadeUp delay={60}>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tighter text-[#F4F4F8] mt-4 mb-5">
              Frequently asked questions.
            </h1>
          </FadeUp>
          <FadeUp delay={120}>
            <p className="text-lg text-[#8B8BA7]">
              Everything you need to know about CleanBid. Can't find your answer?{" "}
              <a href="mailto:info@cleanbid.ai" className="text-[#71DED6] hover:underline">
                Drop us a line.
              </a>
            </p>
          </FadeUp>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="pb-24" data-testid="faq-section">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="space-y-14">
            {FAQ_DATA.map((cat, i) => (
              <FadeUp key={i}>
                <div data-testid={`faq-category-${cat.category.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className={`w-1.5 h-6 rounded-full ${
                        cat.color === "green" ? "bg-[#71DED6]" :
                        cat.color === "indigo" ? "bg-[#6B5CF6]" : "bg-amber-400"
                      }`}
                    />
                    <h2 className="text-xl font-bold text-[#F4F4F8]">{cat.category}</h2>
                  </div>
                  <div>
                    {cat.questions.map((item, j) => (
                      <AccordionItem key={j} question={item.q} answer={item.a} />
                    ))}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 glow-cta border-t border-[#1E1E2E]" data-testid="faq-cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <FadeUp>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F4F4F8] mb-4">
              Still have questions?
            </h2>
          </FadeUp>
          <FadeUp delay={80}>
            <p className="text-[#8B8BA7] mb-8 text-lg">
              Book a demo and ask us anything live. No pressure, no pitch — just answers.
            </p>
          </FadeUp>
          <FadeUp delay={160}>
            <a
              href={CALCOM_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faq-cta-button"
              className="bg-[#71DED6] text-[#10151D] font-semibold px-10 py-4 rounded-full text-lg hover:scale-[1.02] hover:shadow-glow-green transition-transform duration-200 inline-flex items-center gap-2"
            >
              Book a Demo <ArrowRight className="w-5 h-5" />
            </a>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
